// features/settings/pages/PermissionsPage.tsx
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import * as api from '../services/settingsApi';
import {
    ArrowLeft, Shield, Users, Package, ShoppingCart, ClipboardCheck,
    Truck, BarChart3, Settings, CreditCard, UserCheck, Search, Store,
} from 'lucide-react';
import { motion } from 'framer-motion';

interface Permission {
    id: string;
    module: string;
    action: string;
    description: string | null;
}

// User-friendly descriptions for every module
const MODULE_INFO: Record<string, {
    icon: React.ElementType;
    label: string;
    gradient: string;
    description: string;
    color: string;
}> = {
    tenant: { icon: Store, label: 'Business Profile', gradient: 'from-slate-500 to-slate-700', description: 'View and edit your company information, address, and contact details', color: 'border-slate-200 bg-slate-50' },
    users: { icon: Users, label: 'Staff Management', gradient: 'from-blue-500 to-indigo-600', description: 'Create staff accounts, assign roles, and manage team members', color: 'border-blue-200 bg-blue-50' },
    roles: { icon: Shield, label: 'Roles & Access Control', gradient: 'from-purple-500 to-violet-600', description: 'Define what each staff role can do in the system', color: 'border-purple-200 bg-purple-50' },
    products: { icon: Package, label: 'Product Catalog', gradient: 'from-teal-500 to-cyan-600', description: 'Add and manage products, prices, categories, and images', color: 'border-teal-200 bg-teal-50' },
    inventory: { icon: ClipboardCheck, label: 'Inventory & Stock Control', gradient: 'from-emerald-500 to-green-600', description: 'Track stock levels, perform counts, and manage warehouse', color: 'border-emerald-200 bg-emerald-50' },
    procurement: { icon: Truck, label: 'Purchasing & Suppliers', gradient: 'from-amber-500 to-orange-600', description: 'Create purchase orders, receive goods, and manage suppliers', color: 'border-amber-200 bg-amber-50' },
    sales: { icon: ShoppingCart, label: 'Sales & POS Operations', gradient: 'from-rose-500 to-pink-600', description: 'Process sales, apply discounts, handle returns and voids', color: 'border-rose-200 bg-rose-50' },
    customers: { icon: UserCheck, label: 'Customer Records', gradient: 'from-green-500 to-emerald-600', description: 'Manage customer information, history, and loyalty', color: 'border-green-200 bg-green-50' },
    reports: { icon: BarChart3, label: 'Reports & Analytics', gradient: 'from-indigo-500 to-blue-600', description: 'View sales reports, profit & loss, and business analytics', color: 'border-indigo-200 bg-indigo-50' },
    audit: { icon: Search, label: 'Activity & Audit Logs', gradient: 'from-slate-500 to-slate-700', description: 'Review system activity and track all changes made', color: 'border-slate-200 bg-slate-50' },
    settings: { icon: Settings, label: 'System Settings', gradient: 'from-zinc-500 to-zinc-700', description: 'Configure organization preferences and system behavior', color: 'border-zinc-200 bg-zinc-50' },
    banking: { icon: CreditCard, label: 'Banking & Finance', gradient: 'from-cyan-500 to-sky-600', description: 'Manage bank accounts, track balances, and reconcile', color: 'border-cyan-200 bg-cyan-50' },
};

export function PermissionsPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);

    const { data: permissions, isLoading } = useQuery<Permission[]>({
        queryKey: ['permissions'],
        queryFn: () => api.getPermissions(),
        enabled: !!user?.tenantId,
    });

    const groupedPermissions = (permissions || []).reduce((acc: Record<string, Permission[]>, perm) => {
        if (!acc[perm.module]) acc[perm.module] = [];
        acc[perm.module].push(perm);
        return acc;
    }, {});

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-14 h-14 rounded-2xl border-4 border-purple-200 border-t-purple-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/30">
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/settings')} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" type="button">
                            <ArrowLeft className="w-5 h-5 text-slate-600" />
                        </button>
                        <div className="p-2.5 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl shadow-lg shadow-purple-500/25">
                            <Shield className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">System Permissions</h1>
                            <p className="text-sm text-slate-500">
                                {permissions?.length || 0} permissions across {Object.keys(groupedPermissions).length} modules — These are assigned to roles
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(groupedPermissions).map(([module, perms], i) => {
                        const info = MODULE_INFO[module] || { icon: Shield, label: module, gradient: 'from-slate-500 to-slate-700', description: 'System permissions', color: 'border-slate-200 bg-slate-50' };
                        const Icon = info.icon;
                        return (
                            <motion.div key={module} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                whileHover={{ y: -3 }}
                                className={`bg-white rounded-2xl border-2 ${info.color} shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden`}>
                                <div className={`h-1 bg-gradient-to-r ${info.gradient}`} />
                                <div className="p-5">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className={`p-2 rounded-lg bg-gradient-to-br ${info.gradient} shadow-md`}>
                                            <Icon size={18} className="text-white" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-900">{info.label}</h3>
                                            <p className="text-xs text-slate-500">{perms.length} access level{perms.length !== 1 ? 's' : ''}</p>
                                        </div>
                                    </div>
                                    <p className="text-xs text-slate-400 mb-3">{info.description}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {perms.map((perm) => (
                                            <Badge key={perm.id} variant="success" size="sm">
                                                {perm.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}